import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Badge,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  Checkbox,
  SimpleGrid,
  TableContainer,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  MagnifyingGlass,
  FileText,
  CurrencyDollar,
  Receipt,
  Prohibit,
  ArrowSquareOut,
  ArrowSquareIn,
  Stack,
  UsersThree,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, qtd as fmtQtd } from '../format';
import {
  IAuditoriaEmpresa,
  INotaItem,
  INotasPage,
  INotasResumo,
  NotaEspecie,
  NotaTipo,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { NoteDrawer } from './NoteDrawer';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';

interface NotasScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  buscaInicial?: string;
  tipoInicial?: NotaTipo;
  /** Cruzamentos: navegar a outro submódulo (Clientes & Fornecedores, Impostos por Nota). */
  onScreen?: (screen: string) => void;
}

const TIPOS: { value: NotaTipo; label: string }[] = [
  { value: 'saida', label: 'Saída' },
  { value: 'entrada', label: 'Entrada' },
  { value: 'servico', label: 'Serviço' },
];
const TIPO_LABEL: Record<string, string> = {
  saida: 'Saída',
  entrada: 'Entrada',
  servico: 'Serviço',
};
const TIPO_COR: Record<string, string> = { saida: UI.pos, entrada: UI.warn, servico: '#8B5CF6' };
// Especie do documento (modelo) — vem do join com extract_impostos_nota_*.
const ESPECIE_LABEL: Record<string, string> = {
  nfe: 'NF-e',
  nfce: 'NFC-e',
  cte: 'CT-e',
  nfse: 'NFS-e',
  fatura: 'Fatura',
};
const ESPECIE_COR: Record<string, string> = {
  nfe: '#1B88F5',
  nfce: '#0ea5e9',
  cte: '#f59e0b',
  nfse: '#8B5CF6',
  fatura: '#6d28d9',
};

// Status da nota a partir da `situacao`: NULL ou ativa (N/1/A) = Autorizada; senão Cancelada.
// (Mesma regra de "ativas" do backend; entrada vem 100% NULL no dado → Autorizada.)
const statusNota = (s: string | null | undefined): { label: string; cor: string } =>
  s == null || ['N', '1', 'A'].includes(s)
    ? { label: 'Autorizada', cor: UI.pos }
    : { label: 'Cancelada', cor: UI.neg };

/** 'YYYY-MM' → 'MM/AA'. */
const mesLabel = (m: string) => {
  const [y, mo] = (m ?? '').split('-');
  return mo && y ? `${mo}/${y.slice(2)}` : m;
};

const Glass = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    borderRadius="18px"
    p={5}
    bg="rgba(255,255,255,0.7)"
    backdropFilter="blur(14px) saturate(160%)"
    border="1px solid rgba(255,255,255,0.85)"
    boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
  >
    <Flex justify="space-between" align="center" mb={4} gap={2}>
      <Text fontSize="13px" fontWeight={800} color={UI.ink}>
        {title}
      </Text>
      {action}
    </Flex>
    {children}
  </Box>
);

export function NotasScreen({
  empresa,
  periodo,
  buscaInicial,
  tipoInicial,
  onScreen,
}: NotasScreenProps) {
  const { getNotas, getNotasResumo } = useAuditoriaInteligente();
  const [tipo, setTipo] = useState<NotaTipo | ''>(tipoInicial ?? '');
  const [especie, setEspecie] = useState<NotaEspecie | ''>('');
  const [busca, setBusca] = useState(buscaInicial ?? '');
  const [buscaDebounced, setBuscaDebounced] = useState(buscaInicial ?? '');
  const [apenasAtivas, setApenasAtivas] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [drawerNota, setDrawerNota] = useState<INotaItem | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const PAGE = 100;

  const onBusca = (value: string) => {
    setBusca(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setBuscaDebounced(value), 400);
  };

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    if (buscaInicial !== undefined) {
      setBusca(buscaInicial);
      setBuscaDebounced(buscaInicial);
    }
    if (tipoInicial) setTipo(tipoInicial);
  }, [buscaInicial, tipoInicial]);

  const tipoParam = tipo || undefined;

  // Agregados do período inteiro (KPIs + gráficos), respeitando tipo/ativas.
  const { data: resumo, isFetching: loadingResumo } = useQuery<INotasResumo>({
    queryKey: [
      'ai-notas-resumo',
      { id: empresa?.id, tipo: tipoParam, apenas_ativas: apenasAtivas, ...periodo },
    ],
    queryFn: getNotasResumo,
    enabled: !!empresa,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  // Volta pra 1ª página quando qualquer filtro muda.
  useEffect(() => {
    setPagina(0);
  }, [tipoParam, especie, buscaDebounced, apenasAtivas, empresa?.id]);

  // Listagem detalhada paginada (100 por página), com busca/tipo/especie.
  const { data: notasPage, isFetching } = useQuery<INotasPage>({
    queryKey: [
      'ai-notas',
      {
        id: empresa?.id,
        tipo: tipoParam,
        especie: especie || undefined,
        busca: buscaDebounced || undefined,
        apenas_ativas: apenasAtivas,
        limit: PAGE,
        offset: pagina * PAGE,
        ...periodo,
      },
    ],
    queryFn: getNotas,
    enabled: !!empresa,
    placeholderData: keepPreviousData,
  });
  const notas = notasPage?.itens ?? [];
  const totalNotas = notasPage?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalNotas / PAGE));

  const t = resumo?.totais;
  /** Valor somado das notas de um tipo (saída/entrada) — segrega o "valor total". */
  const valPorTipo = (tp: string) => num((resumo?.por_tipo ?? []).find(x => x.tipo === tp)?.valor);
  /** Quantidade de notas de um tipo (serviço não tem valor próprio → mostra qtd). */
  const qtdPorTipo = (tp: string) => num((resumo?.por_tipo ?? []).find(x => x.tipo === tp)?.qtd);
  /** Receita financeira embutida na soma bruta de um tipo (rendimento de aplicação
   *  lançado como saída, mas não é venda). */
  const receitaFinPorTipo = (tp: string) => {
    const r = (resumo?.por_tipo ?? []).find(x => x.tipo === tp);
    return { valor: num(r?.valor_receita_fin), qtd: r?.qtd_receita_fin ?? 0 };
  };
  const recFinSaida = receitaFinPorTipo('saida');
  const mesData = useMemo(
    () =>
      (resumo?.por_mes ?? []).map(m => ({ mes: mesLabel(m.mes), qtd: m.qtd, valor: num(m.valor) })),
    [resumo],
  );
  const maxParceiro = Math.max(...(resumo?.top_parceiros ?? []).map(p => num(p.valor)), 1);

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver as notas fiscais."
        icon={FileText}
      />
    );
  }

  const lista = notas ?? [];

  return (
    <Box>
      {/* KPIs */}
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={4} mb={5}>
        <KpiCard
          label="Notas no período"
          value={fmtQtd(t?.qtd ?? 0)}
          sub={
            (resumo?.por_tipo ?? [])
              .map(x => `${fmtQtd(x.qtd)} ${TIPO_LABEL[x.tipo] ?? x.tipo}`)
              .join(' · ') || undefined
          }
          icon={FileText}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={loadingResumo}
          info={
            <InfoBloco
              oque="Quantidade de notas fiscais emitidas/recebidas pela empresa no período (saída, entrada e serviço somadas). O subtítulo abre a contagem por tipo."
              dados="COUNT(*) sobre a união de extract_notas_saida, extract_notas_entrada e extract_notas_servico (filtra por data_saida/data_entrada/data_servico)."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Entrada"
          value={formatCurrency(valPorTipo('entrada'))}
          sub="notas de entrada (compras)"
          icon={ArrowSquareIn}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={loadingResumo}
          info={
            <InfoBloco
              oque="Soma do valor das notas fiscais de entrada no período (total de compras). Segregado das saídas para não misturar com a receita."
              dados="por_tipo: SUM(extract_notas_entrada.valor) WHERE tipo = 'entrada'."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Saída"
          value={formatCurrency(valPorTipo('saida'))}
          sub="notas de saída (soma bruta)"
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={loadingResumo}
          footer={
            recFinSaida.valor > 0 && (
              <Badge
                mt={2}
                bg="rgba(245,158,11,0.15)"
                color="#b45309"
                borderRadius="6px"
                px={1.5}
                fontSize="10px"
                fontWeight={700}
                textTransform="none"
                whiteSpace="normal"
                title="Rendimento de aplicação financeira (acumulador Receitas Financeiras). O Domínio lança como saída, então entra nesta soma bruta, mas não é venda — não conta como faturamento."
              >
                inclui {formatCurrency(recFinSaida.valor)} de receita financeira
              </Badge>
            )
          }
          info={
            <InfoBloco
              oque="Soma BRUTA do valor das notas fiscais de saída no período — inclui devoluções/remessas E receita financeira (rendimento de aplicação lançado como saída), então NÃO é o faturamento oficial (esse segue a regra do Domínio, acumuladores IFAT = S). Segregado das entradas para não misturar receita com compra."
              dados="por_tipo: SUM(valor) WHERE tipo = 'saida'. valor_receita_fin: SUM(valor) FILTER (WHERE espécie não-eletrônica — codi_esp 45/49/53/43/48)."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Serviço"
          value={formatCurrency(valPorTipo('servico'))}
          sub={`${fmtQtd(qtdPorTipo('servico'))} notas de serviço (NFS-e)`}
          icon={Stack}
          iconColor="#1AA6B7"
          iconBg="#E2F4F6"
          isLoading={loadingResumo}
          info={
            <InfoBloco
              oque="Soma do valor das notas fiscais de serviço (NFS-e) no período. A tabela de notas de serviço não tem coluna de valor próprio — o valor vem do valor_nota (distinto por nota) da tabela de impostos por nota de serviço."
              dados="por_tipo['servico'].valor: SUM(extract_impostos_nota_servico.valor_nota) DISTINCT por nota, ligado à extract_notas_servico por numero_nf/serie."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Ticket médio"
          value={formatCurrency(num(t?.ticket_medio))}
          icon={Receipt}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={loadingResumo}
          info={
            <InfoBloco
              oque="Valor médio por nota no período: valor total dividido pela quantidade de notas que possuem valor (notas sem valor, como serviço, ficam de fora da média)."
              dados="SUM(valor) FILTER (WHERE valor IS NOT NULL) ÷ COUNT(*) FILTER (WHERE valor IS NOT NULL) sobre extract_notas_saida/_entrada."
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Canceladas"
          value={fmtQtd(t?.qtd_canceladas ?? 0)}
          sub={apenasAtivas ? 'exibir todas →' : 'incluídas'}
          icon={Prohibit}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={loadingResumo}
          onClick={() => setApenasAtivas(v => !v)}
          info={
            <InfoBloco
              oque="Notas com situação fora das ativas (canceladas/denegadas) no período. Clicar no card alterna entre exibir só ativas ou incluir todas na contagem."
              dados="COUNT(*) FILTER (WHERE situacao IS NOT NULL AND situacao NOT IN ('N','1','A')) sobre extract_notas_saida/_entrada.situacao."
              filtro={FILTRO.empresa}
            />
          }
        />
      </SimpleGrid>

      {/* Gráficos */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5} mb={5}>
        <Glass title="Volume e valor por mês">
          {mesData.length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem notas no período.
            </Text>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={mesData}>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                <YAxis yAxisId="l" fontSize={11} stroke={UI.muted} allowDecimals={false} />
                <YAxis
                  yAxisId="r"
                  orientation="right"
                  fontSize={11}
                  stroke={UI.muted}
                  tickFormatter={v => formatCurrency(v)}
                  width={70}
                />
                <Tooltip formatter={(v: number, n) => (n === 'Valor' ? formatCurrency(v) : v)} />
                <Legend />
                <Bar yAxisId="l" dataKey="qtd" name="Notas" fill={UI.brand} radius={[4, 4, 0, 0]} />
                <Line
                  yAxisId="r"
                  type="monotone"
                  dataKey="valor"
                  name="Valor"
                  stroke={UI.pos}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Glass>

        <Glass
          title="Top parceiros"
          action={
            onScreen && (
              <Button
                size="xs"
                variant="ghost"
                color={UI.brand}
                rightIcon={<ArrowSquareOut size={13} weight="bold" />}
                onClick={() => onScreen('clientes-fornecedores')}
              >
                Clientes & Fornecedores
              </Button>
            )
          }
        >
          {(resumo?.top_parceiros ?? []).length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem parceiros no período.
            </Text>
          ) : (
            (resumo?.top_parceiros ?? []).map(p => (
              <Flex
                key={`${p.parceiro}-${p.parceiro_id}`}
                align="center"
                gap={3}
                mb={2.5}
                cursor="pointer"
                onClick={() => onBusca(p.parceiro)}
                title={`Filtrar notas de ${p.parceiro}`}
                _hover={{ opacity: 0.85 }}
              >
                <Box minW={0} flex={1}>
                  <Text fontSize="12px" fontWeight={700} color={UI.ink} noOfLines={1}>
                    {p.parceiro}
                  </Text>
                  <Box
                    h="6px"
                    mt={1}
                    borderRadius="full"
                    bg="rgba(15,23,42,0.06)"
                    overflow="hidden"
                  >
                    <Box
                      h="100%"
                      borderRadius="full"
                      bgGradient={`linear(to-r, ${UI.brand}, #1AA6B7)`}
                      w={`${(num(p.valor) / maxParceiro) * 100}%`}
                    />
                  </Box>
                </Box>
                <Box textAlign="right" flexShrink={0}>
                  <Text fontSize="12.5px" fontWeight={800} color={UI.ink}>
                    {formatCurrency(num(p.valor))}
                  </Text>
                  <Text fontSize="10.5px" color={UI.faint}>
                    {fmtQtd(p.qtd)} notas
                  </Text>
                </Box>
              </Flex>
            ))
          )}
        </Glass>
      </SimpleGrid>

      {/* Filtros + listagem */}
      <Glass
        title="Notas (últimas 100)"
        action={
          onScreen && (
            <Button
              size="xs"
              variant="ghost"
              color={UI.brand}
              rightIcon={<ArrowSquareOut size={13} weight="bold" />}
              onClick={() => onScreen('impostos-por-nota')}
            >
              Impostos por nota
            </Button>
          )
        }
      >
        <Flex gap={3} align="center" wrap="wrap" mb={4}>
          <Select
            size="sm"
            w="150px"
            borderRadius="10px"
            bg="white"
            value={tipo}
            onChange={e => setTipo(e.target.value as NotaTipo | '')}
          >
            <option value="">Todos os tipos</option>
            {TIPOS.map(x => (
              <option key={x.value} value={x.value}>
                {x.label}
              </option>
            ))}
          </Select>
          <Select
            size="sm"
            w="150px"
            borderRadius="10px"
            bg="white"
            value={especie}
            onChange={e => setEspecie(e.target.value as NotaEspecie | '')}
          >
            <option value="">Todas as espécies</option>
            <option value="nfe">NF-e</option>
            <option value="nfce">NFC-e</option>
            <option value="cte">CT-e</option>
            <option value="nfse">NFS-e</option>
          </Select>
          <InputGroup size="sm" w={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none" color={UI.muted}>
              <MagnifyingGlass size={15} />
            </InputLeftElement>
            <Input
              placeholder="NF, parceiro, CNPJ…"
              value={busca}
              onChange={e => onBusca(e.target.value)}
              borderRadius="10px"
              bg="white"
            />
          </InputGroup>
          <Checkbox
            isChecked={apenasAtivas}
            onChange={e => setApenasAtivas(e.target.checked)}
            size="sm"
          >
            Apenas ativas
          </Checkbox>
        </Flex>

        <Box border="1px solid" borderColor={UI.border} borderRadius="14px" overflow="hidden">
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>NF / Série</Th>
                  <Th>Espécie</Th>
                  <Th>Tipo</Th>
                  <Th>Data</Th>
                  <Th>Parceiro</Th>
                  <Th>Situação</Th>
                  <Th isNumeric>Valor</Th>
                </Tr>
              </Thead>
              <Tbody>
                {isFetching && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}
                {!isFetching &&
                  lista.map(n => (
                    <Tr
                      key={`${n.tipo}-${n.codigo}`}
                      cursor="pointer"
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => setDrawerNota(n)}
                    >
                      <Td fontWeight={700}>
                        {n.numero_nf}
                        {n.serie ? ` / ${n.serie}` : ''}
                      </Td>
                      <Td>
                        {n.especie ? (
                          <Badge
                            style={{
                              background: `${ESPECIE_COR[n.especie] ?? UI.muted}1A`,
                              color: ESPECIE_COR[n.especie] ?? UI.muted,
                            }}
                            borderRadius="full"
                            px={2}
                            fontSize="10px"
                            fontWeight={700}
                            textTransform="none"
                          >
                            {ESPECIE_LABEL[n.especie] ?? n.especie}
                          </Badge>
                        ) : (
                          <Text color={UI.muted} fontSize="12px">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Badge
                          style={{ background: `${TIPO_COR[n.tipo]}1A`, color: TIPO_COR[n.tipo] }}
                          borderRadius="full"
                          px={2}
                          fontSize="10px"
                          fontWeight={700}
                          textTransform="none"
                        >
                          {TIPO_LABEL[n.tipo] ?? n.tipo}
                        </Badge>
                      </Td>
                      <Td>{n.data}</Td>
                      <Td
                        maxW="260px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {n.parceiro}
                      </Td>
                      <Td>
                        {((): React.ReactNode => {
                          const st = statusNota(n.situacao);
                          return (
                            <Badge
                              style={{ background: `${st.cor}1A`, color: st.cor }}
                              borderRadius="full"
                              px={2}
                              fontSize="10px"
                              fontWeight={700}
                              textTransform="none"
                            >
                              {st.label}
                            </Badge>
                          );
                        })()}
                      </Td>
                      <Td isNumeric fontWeight={700}>
                        {n.valor == null ? '—' : formatCurrency(n.valor)}
                      </Td>
                    </Tr>
                  ))}
                {!isFetching && lista.length === 0 && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={8} color={UI.muted}>
                      <Flex direction="column" align="center" gap={2}>
                        <UsersThree size={28} />
                        Nenhuma nota encontrada no período.
                      </Flex>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
        {totalPaginas > 1 && (
          <Flex justify="space-between" align="center" mt={3} fontSize="12.5px" color={UI.muted}>
            <Text>
              {totalNotas.toLocaleString('pt-BR')} nota(s) · página <b>{pagina + 1}</b> de{' '}
              <b>{totalPaginas}</b>
            </Text>
            <Flex gap={2}>
              <Button
                size="xs"
                variant="ghost"
                isDisabled={pagina === 0 || isFetching}
                onClick={() => setPagina(p => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="xs"
                variant="ghost"
                isDisabled={pagina + 1 >= totalPaginas || isFetching}
                onClick={() => setPagina(p => p + 1)}
              >
                Próxima
              </Button>
            </Flex>
          </Flex>
        )}
      </Glass>

      {drawerNota && (
        <NoteDrawer
          empresaId={empresa.id}
          tipo={drawerNota.tipo}
          codigo={drawerNota.codigo}
          isOpen={!!drawerNota}
          onClose={() => setDrawerNota(null)}
        />
      )}
    </Box>
  );
}
